import React, { useEffect, useMemo, useState } from "react";
import {
  FIELD_GROUPS, CATEGORIES, OPERATORS, RULE_TYPES,
  newCond, newGroup, fieldDef, buildRuleJSON, validateRule, evalGroup,
  makeTemplate, TEMPLATES, SAMPLE_CONTEXT,
  getActionTargetFields,
  enrichFieldGroups, resolveOperators, parseTimeRangeValue,
} from "./engine";
import { evaluateManual } from "./billing-rules-api";
import { searchChargeCaptureServices } from "../ipd-billing/billing-api";
import { Btn, Field, TabBar, cardStyle, inputStyle, labelStyle, C, RuleTypeChip } from "./ui";
import SvgIcon from "../ipd-billing/SvgIcon";
import { Modal } from "./ui";

const ACTION_KINDS = [
  { key: "price_override", label: "Price Override" },
  { key: "discount", label: "Discount" },
  { key: "tax", label: "Tax" },
  { key: "markup", label: "Markup" },
  { key: "approval", label: "Approval" },
  { key: "remark", label: "Remark" },
  { key: "set_billing_category", label: "Set Billing Category" },
  { key: "doctor_payout", label: "Doctor Payout" },
];

const SIM_PRESETS = {
  "OPD senior": { patient: { age: 67, gender: "FEMALE", metadata: { vip: false } }, encounter: { encounter_type: "OPD", payer_type: "SELF" }, charge_event: { category: "CONSULTATION", service_code: "GENERAL-CONSULT", unit_price: 1000, quantity: 1, gross_amount: 1000 } },
  "IPD TPA ICU": { patient: { age: 54, gender: "MALE", metadata: { vip: false } }, encounter: { encounter_type: "IPD", payer_type: "TPA" }, charge_event: { category: "ROOM_RENT", service_code: "ICU-BED-DAY", unit_price: 14500, quantity: 1, gross_amount: 14500 } },
  VIP: { patient: { age: 42, gender: "MALE", metadata: { vip: true } }, encounter: { encounter_type: "IPD", payer_type: "SELF" }, charge_event: { category: "PROCEDURE", service_code: "MRI-BRAIN", unit_price: 12000, quantity: 1, gross_amount: 12000 } },
  "High value": { patient: { age: 50, gender: "MALE", metadata: { vip: false } }, encounter: { encounter_type: "IPD", payer_type: "INSURANCE" }, charge_event: { category: "PROCEDURE", service_code: "PCI", unit_price: 95000, quantity: 1, gross_amount: 95000 } },
};

function updateNode(root, id, fn) {
  const recur = (n) => {
    if (n.id === id) return fn(n);
    if (n.kind === "group") {
      const next = [];
      for (const c of n.children) {
        const r = recur(c);
        if (r) next.push(r);
      }
      return { ...n, children: next };
    }
    return n;
  };
  return recur(root);
}

function addChildToGroup(root, groupId, child) {
  return updateNode(root, groupId, (n) => {
    if (n.kind !== "group") return n;
    return { ...n, children: [...n.children, child] };
  });
}

const SERVICE_FIELDS = ["charge_event.service_code", "charge_event.service_name"];

/**
 * Searchable picker for a real billing service, so a service condition value is
 * chosen (not free-typed and mistyped). For the "Service Code" field it stores the
 * service_code; for "Service Name" it stores the service name. Uses the same
 * charge-capture services list the billing screen uses.
 */
function ServiceValueInput({ field, value, onChange }) {
  const useCode = field === "charge_event.service_code";
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const list = await searchChargeCaptureServices(q, { limit: 40 });
        // De-duplicate so one clinical service shows once, not once per bed variant.
        // Bed variants share the same name (differ only by service_code): key the
        // "Service Name" list by name, the "Service Code" list by code.
        const seen = new Set();
        const uniq = [];
        (Array.isArray(list) ? list : []).forEach((s) => {
          const key = useCode ? (s.service_code || s.code || "") : (s.name || "");
          if (!key || seen.has(key)) return;
          seen.add(key);
          uniq.push(s);
        });
        if (alive) setOpts(uniq);
      } catch (e) {
        if (alive) setOpts([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  const pick = (s) => {
    const v = useCode ? (s.service_code || s.code || "") : (s.name || "");
    onChange(v);
    setQ(v);
    setOpen(false);
  };

  return (
    <div style={{ position: "relative", width: 220 }}>
      <input
        // Combobox: typing sets the value directly (still works if the list can't
        // load / the service isn't listed); picking a suggestion fills the exact one.
        value={open ? q : String(value ?? "")}
        onFocus={() => { setQ(String(value ?? "")); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setQ(e.target.value); onChange(e.target.value); }}
        placeholder={useCode ? "Search / type service code…" : "Search / type service name…"}
        style={{ ...inputStyle, width: "100%" }}
      />
      {open && (
        <div style={{ position: "absolute", zIndex: 30, top: "100%", left: 0, right: 0, maxHeight: 220, overflowY: "auto", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,.12)" }}>
          {loading && <div style={{ padding: 8, fontSize: 11, color: C.textSub }}>Searching…</div>}
          {!loading && opts.length === 0 && <div style={{ padding: 8, fontSize: 11, color: C.textSub }}>No services found</div>}
          {opts.map((s) => (
            <div
              key={s.id || s.service_code || s.name}
              onMouseDown={() => pick(s)}
              style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", borderBottom: `1px solid ${C.border}` }}
            >
              <div style={{ fontWeight: 600 }}>{s.name}</div>
              <div style={{ fontFamily: "monospace", fontSize: 10, color: C.textSub }}>{useCode ? (s.service_code || s.code || "") : "all bed types"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ValueInput({ cond, onChange, operators }) {
  const def = fieldDef(cond.field);
  const op = (operators ?? OPERATORS).find((o) => o.code === cond.operator);
  if (!op || op.arity === "none") return null;

  if (op.arity === "time_range") {
    const range = parseTimeRangeValue(cond.value);
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          type="time"
          value={range.from}
          onChange={(e) => onChange({ from: e.target.value, to: range.to })}
          style={{ ...inputStyle, width: 110 }}
        />
        <span style={{ fontSize: 11, color: C.textSub }}>to</span>
        <input
          type="time"
          value={range.to}
          onChange={(e) => onChange({ from: range.from, to: e.target.value })}
          style={{ ...inputStyle, width: 110 }}
        />
      </div>
    );
  }

  if (op.arity === "list") {
    return (
      <input
        value={Array.isArray(cond.value) ? cond.value.join(",") : String(cond.value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        placeholder="comma,separated"
        style={{ ...inputStyle, width: 200 }}
      />
    );
  }
  if (op.arity === "two") {
    const arr = Array.isArray(cond.value) ? cond.value : String(cond.value ?? "").split(",");
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input value={String(arr[0] ?? "")} onChange={(e) => onChange([e.target.value, arr[1] ?? ""])} placeholder="from" style={{ ...inputStyle, width: 90 }} />
        <span style={{ fontSize: 11, color: C.textSub }}>to</span>
        <input value={String(arr[1] ?? "")} onChange={(e) => onChange([arr[0] ?? "", e.target.value])} placeholder="to" style={{ ...inputStyle, width: 90 }} />
      </div>
    );
  }
  if (def?.type === "boolean") {
    return (
      <select value={String(cond.value)} onChange={(e) => onChange(e.target.value === "true")} style={{ ...inputStyle, width: 100 }}>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (def?.type === "enum" && def.enumValues) {
    return (
      <select value={String(cond.value ?? "")} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, width: 160 }}>
        <option value="">Pick…</option>
        {def.enumValues.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
    );
  }
  if (def?.type === "number") {
    return <input type="number" value={String(cond.value ?? "")} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} style={{ ...inputStyle, width: 120 }} />;
  }
  if (def?.type === "date") {
    return <input type="date" value={String(cond.value ?? "")} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, width: 150 }} />;
  }
  if (def?.type === "datetime") {
    return <input type="datetime-local" value={String(cond.value ?? "")} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, width: 190 }} />;
  }
  // Service Code / Service Name → pick a real service instead of free-typing it
  // (single-value operators only; list/range operators are handled above).
  if (SERVICE_FIELDS.includes(cond.field)) {
    return <ServiceValueInput field={cond.field} value={cond.value} onChange={onChange} />;
  }
  return <input value={String(cond.value ?? "")} onChange={(e) => onChange(e.target.value)} placeholder="value" style={{ ...inputStyle, width: 160 }} />;
}

function defaultValueForOperator(opCode) {
  const op = OPERATORS.find((o) => o.code === opCode);
  if (op?.arity === "time_range") return { from: "", to: "" };
  if (op?.arity === "two") return ["", ""];
  if (op?.arity === "list") return "";
  return "";
}

function CondRow({ cond, onChange, fieldGroups, operators }) {
  const def = fieldDef(cond.field);
  const op = operators.find((o) => o.code === cond.operator);
  return (
    <div style={{ ...cardStyle, padding: 10, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", opacity: cond.enabled ? 1 : 0.5 }}>
      <input type="checkbox" checked={cond.enabled} onChange={(e) => onChange({ ...cond, enabled: e.target.checked })} />
      <select value={cond.field || ""} onChange={(e) => onChange({ ...cond, field: e.target.value, value: "" })} style={{ ...inputStyle, width: 200, fontSize: 12 }}>
        <option value="">Pick field…</option>
        {fieldGroups.map((g) => (
          <optgroup key={g.key} label={g.label}>
            {g.fields.map((f) => <option key={f.path} value={f.path}>{f.label}</option>)}
          </optgroup>
        ))}
      </select>
      <select
        value={cond.operator}
        onChange={(e) => {
          const nextOp = e.target.value;
          onChange({
            ...cond,
            operator: nextOp,
            value: nextOp === cond.operator ? cond.value : defaultValueForOperator(nextOp),
          });
        }}
        style={{ ...inputStyle, width: 170, fontSize: 12 }}
      >
        {operators.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
      </select>
      <ValueInput cond={cond} operators={operators} onChange={(v) => onChange({ ...cond, value: v })} />
      {def && <span style={{ fontSize: 10, fontWeight: 700, color: C.textSub, textTransform: "uppercase" }}>{def.type}</span>}
      {op?.arity === "none" && <span style={{ fontSize: 10, color: C.textSub }}>no value</span>}
      <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
        <Btn variant="ghost" size="sm" onClick={() => onChange(null)}><SvgIcon name="trash" size={14} color={C.danger} /></Btn>
      </div>
    </div>
  );
}

function GroupView({ node, onChange, depth = 0, isRoot, fieldGroups, operators }) {
  const [dragOver, setDragOver] = useState(false);
  const tone = depth % 2 === 0 ? { border: "#bae6fd", bg: "#f0f9ff" } : { border: "#99f6e4", bg: "#f0fdfa" };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const path = e.dataTransfer.getData("application/x-rule-field");
    if (!path) return;
    onChange(addChildToGroup(node, node.id, newCond(path, "eq")));
  };

  const updateChild = (childId, next) => {
    onChange(updateNode(node, childId, () => next));
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      style={{
        border: `2px dashed ${dragOver ? C.primary : tone.border}`,
        background: tone.bg, borderRadius: 10, padding: 12,
        outline: dragOver ? `2px solid ${C.primary}` : "none",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <select value={node.type} onChange={(e) => onChange({ ...node, type: e.target.value })} style={{ ...inputStyle, width: 90, fontWeight: 700 }}>
          <option value="all">AND</option>
          <option value="any">OR</option>
          <option value="not">NOT</option>
        </select>
        <span style={{ fontSize: 11, color: C.textSub }}>
          {node.type === "all" ? "All must match" : node.type === "any" ? "Any can match" : "None must match"}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <Btn variant="ghost" size="sm" onClick={() => onChange({ ...node, children: [...node.children, newCond()] })}>
            <SvgIcon name="plus" size={12} color={C.primary} /> Condition
          </Btn>
          <Btn variant="ghost" size="sm" onClick={() => onChange({ ...node, children: [...node.children, newGroup("all")] })}>
            <SvgIcon name="plus" size={12} color={C.primary} /> Group
          </Btn>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {node.children.length === 0 && (
          <div style={{ textAlign: "center", padding: 20, fontSize: 12, color: C.textSub, border: `1px dashed ${C.border}`, borderRadius: 8 }}>
            Drop a field here or add a condition
          </div>
        )}
        {node.children.map((c) =>
          c.kind === "cond" ? (
            <CondRow key={c.id} cond={c} onChange={(next) => updateChild(c.id, next)} fieldGroups={fieldGroups} operators={operators} />
          ) : (
            <GroupView key={c.id} node={c} onChange={(g) => updateChild(c.id, g)} depth={depth + 1} fieldGroups={fieldGroups} operators={operators} />
          ),
        )}
      </div>
    </div>
  );
}

function FieldsSidebar({ fieldGroups, categories }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const groups = fieldGroups.filter((g) => cat === "all" || g.category === cat);

  return (
    <aside style={{ ...cardStyle, display: "flex", flexDirection: "column", maxHeight: 520 }}>
      <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Billing Data Fields</div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search field…" style={{ ...inputStyle, marginBottom: 8 }} />
        <select value={cat} onChange={(e) => setCat(e.target.value)} style={inputStyle}>
          <option value="all">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
        {groups.map((g) => {
          const fields = g.fields.filter((f) =>
            !q || f.label.toLowerCase().includes(q.toLowerCase()) || f.path.toLowerCase().includes(q.toLowerCase()),
          );
          if (!fields.length) return null;
          return (
            <div key={g.key} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textSub, padding: "4px 6px" }}>{g.label}</div>
              {fields.map((f) => (
                <div
                  key={f.path}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("application/x-rule-field", f.path);
                    e.dataTransfer.effectAllowed = "copy";
                  }}
                  style={{
                    padding: "8px 10px", marginBottom: 4, borderRadius: 8, border: `1px solid ${C.border}`,
                    background: "#fff", cursor: "grab", fontSize: 11,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{f.label}</div>
                  <div style={{ fontFamily: "monospace", fontSize: 10, color: C.textSub }}>{f.path}</div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function BlocksSidebar() {
  return (
    <aside style={{ ...cardStyle, padding: 12, fontSize: 11 }}>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>Building Blocks</div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ ...labelStyle, marginBottom: 6 }}>Connectors</div>
        <div style={{ display: "flex", gap: 6 }}>
          {["AND", "OR", "NOT"].map((l) => (
            <span key={l} style={{ flex: 1, textAlign: "center", padding: "6px 0", borderRadius: 6, background: C.accent, color: C.primary, fontWeight: 700 }}>{l}</span>
          ))}
        </div>
      </div>
      <div>
        <div style={{ ...labelStyle, marginBottom: 6 }}>Operators</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {OPERATORS.map((o) => (
            <span key={o.code} style={{ padding: "2px 6px", borderRadius: 4, background: "#f1f5f9", fontSize: 10 }}>{o.label}</span>
          ))}
        </div>
      </div>
    </aside>
  );
}

function MetadataForm({ rule, update, ruleTypes }) {
  return (
    <div style={{ ...cardStyle, padding: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Rule Metadata</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
        <Field label="Rule Code *"><input value={rule.rule_code} onChange={(e) => update({ rule_code: e.target.value })} style={{ ...inputStyle, fontFamily: "monospace" }} /></Field>
        <Field label="Rule Name *"><input value={rule.rule_name} onChange={(e) => update({ rule_name: e.target.value })} style={inputStyle} /></Field>
        <Field label="Rule Type *">
          <select value={rule.rule_type} onChange={(e) => update({ rule_type: e.target.value })} style={inputStyle}>
            {ruleTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Priority *"><input type="number" value={rule.priority} onChange={(e) => update({ priority: Number(e.target.value) })} style={inputStyle} /></Field>
        <Field label="Effective From *"><input type="date" value={rule.effective_from} onChange={(e) => update({ effective_from: e.target.value })} style={inputStyle} /></Field>
        <Field label="Effective To"><input type="date" value={rule.effective_to ?? ""} onChange={(e) => update({ effective_to: e.target.value || null })} style={inputStyle} /></Field>
        <Field label="Description" style={{ gridColumn: "1 / -1" }}>
          <textarea value={rule.description} onChange={(e) => update({ description: e.target.value })} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
        </Field>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={rule.is_active} onChange={(e) => update({ is_active: e.target.checked })} /> Active
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={rule.stop_processing} onChange={(e) => update({ stop_processing: e.target.checked })} /> Stop Processing
        </label>
        <Field label="metadata.business_policy" style={{ gridColumn: "1 / -1" }}>
          <input
            value={rule.metadata?.business_policy ?? ""}
            onChange={(e) => {
              const meta = { ...(rule.metadata || {}), source: rule.metadata?.source ?? "rule_builder_ui" };
              const bp = e.target.value.trim();
              if (bp) meta.business_policy = bp;
              else delete meta.business_policy;
              update({ metadata: meta });
            }}
            style={{ ...inputStyle, fontFamily: "monospace", fontSize: 12 }}
          />
        </Field>
      </div>
    </div>
  );
}

function TargetFieldSelect({ value, onChange, actionTargetFields, hint }) {
  const target = value ?? "";
  const groups = useMemo(() => {
    const order = [];
    const map = {};
    actionTargetFields.forEach((f) => {
      if (!map[f.group]) {
        map[f.group] = [];
        order.push(f.group);
      }
      map[f.group].push(f);
    });
    return order.map((g) => ({ label: g, fields: map[g] }));
  }, [actionTargetFields]);

  return (
    <Field label="Target field *">
      <select value={target} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
        {!target && <option value="">Select field…</option>}
        {target && !actionTargetFields.some((f) => f.path === target) && (
          <option value={target}>{target} (saved)</option>
        )}
        {groups.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.fields.map((f) => (
              <option key={f.path} value={f.path}>
                {f.label} — {f.path} ({f.type})
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {hint && <div style={{ fontSize: 10, color: C.textSub, marginTop: 4 }}>{hint}</div>}
    </Field>
  );
}

function ActionEditor({ kind, value, onChange, actionTargetFields }) {
  const v = value ?? {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const row = (k, label, props = {}) => (
    <Field label={label}><input value={v[k] ?? ""} onChange={(e) => set(k, e.target.value)} style={inputStyle} {...props} /></Field>
  );

  const withTarget = (body, hint) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <TargetFieldSelect
        value={v.target_field ?? ""}
        onChange={(path) => set("target_field", path)}
        actionTargetFields={actionTargetFields}
        hint={hint}
      />
      {body}
    </div>
  );

  if (kind === "price_override") {
    return withTarget(
      <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {row("unit_price", "Unit price *", { type: "number", step: "0.01" })}
          {row("currency_code", "Currency (optional)")}
        </div>
        <Field label="Reason">
          <textarea
            value={v.reason ?? ""}
            onChange={(e) => set("reason", e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </Field>
      </>,
      "Field whose value this action sets (usually unit price on the charge line).",
    );
  }
  if (kind === "discount") {
    return withTarget(
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <Field label="Discount type *">
          <select value={v.discount_type ?? "PERCENTAGE"} onChange={(e) => set("discount_type", e.target.value)} style={inputStyle}>
            <option value="PERCENTAGE">PERCENTAGE</option>
            <option value="FIXED">FIXED</option>
          </select>
        </Field>
        {row("discount_value", "Value *", { type: "number" })}
        {row("max_discount_amount", "Max amount (optional)", { type: "number" })}
        {row("reason", "Reason")}
      </div>,
      "Amount field the discount is calculated against.",
    );
  }
  if (kind === "tax") {
    return withTarget(
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {row("tax_percentage", "Tax % *", { type: "number" })}
        {row("tax_type", "Tax type")}
        {row("reason", "Reason")}
      </div>,
      "Field the tax is applied to.",
    );
  }
  if (kind === "markup") {
    return withTarget(
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {row("markup_percentage", "Markup % *", { type: "number" })}
        {row("reason", "Reason")}
      </div>,
      "Field the markup adjusts.",
    );
  }
  if (kind === "approval") {
    return withTarget(
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Required *">
          <select value={String(v.approval_required ?? true)} onChange={(e) => set("approval_required", e.target.value === "true")} style={inputStyle}>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        </Field>
        {row("approval_role", "Approval role *")}
        {row("approval_reason", "Reason")}
      </div>,
      "Charge field that triggers approval.",
    );
  }
  if (kind === "remark") {
    return withTarget(
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
        <Field label="Message *">
          <textarea value={v.message ?? ""} onChange={(e) => set("message", e.target.value)} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
        </Field>
        <Field label="Severity *">
          <select value={v.severity ?? "INFO"} onChange={(e) => set("severity", e.target.value)} style={inputStyle}>
            <option value="INFO">INFO</option>
            <option value="WARNING">WARNING</option>
            <option value="ERROR">ERROR</option>
          </select>
        </Field>
      </div>,
      "Context field this remark is attached to.",
    );
  }
  if (kind === "set_billing_category") {
    return withTarget(
      row("category", "Category value *"),
      "Field to write the billing category into.",
    );
  }
  if (kind === "doctor_payout") {
    // No target_field — the payee comes from recipient_provider_field.
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Doctor role *">
            <select value={v.payout_role ?? "ADMITTING_DOCTOR"} onChange={(e) => set("payout_role", e.target.value)} style={inputStyle}>
              <option value="ADMITTING_DOCTOR">ADMITTING_DOCTOR</option>
              <option value="REFERRING_DOCTOR">REFERRING_DOCTOR</option>
              <option value="PERFORMING_DOCTOR">PERFORMING_DOCTOR</option>
            </select>
          </Field>
          <Field label="Pay who (recipient field) *">
            <select value={v.recipient_provider_field ?? "encounter.provider_id"} onChange={(e) => set("recipient_provider_field", e.target.value)} style={inputStyle}>
              <option value="encounter.provider_id">Admitting doctor (encounter.provider_id)</option>
              <option value="charge_event.referred_by_provider_id">Referred doctor (charge_event.referred_by_provider_id)</option>
              <option value="charge_event.performed_by_provider_id">Performing doctor (charge_event.performed_by_provider_id)</option>
            </select>
          </Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Payout type *">
            <select value={v.payout_type ?? "PERCENTAGE"} onChange={(e) => set("payout_type", e.target.value)} style={inputStyle}>
              <option value="PERCENTAGE">PERCENTAGE</option>
              <option value="FIXED">FIXED</option>
            </select>
          </Field>
          {row("payout_value", "Value * (% or amount)", { type: "number", step: "0.01" })}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Calculated on (basis) *">
            <select value={v.basis_amount ?? "ledger.net_amount"} onChange={(e) => set("basis_amount", e.target.value)} style={inputStyle}>
              <option value="ledger.net_amount">Net amount (what patient pays)</option>
              <option value="ledger.gross_amount">Gross amount (ledger)</option>
              <option value="charge_event.gross_amount">Charge gross amount</option>
              <option value="charge_event.unit_price">Charge unit price</option>
              <option value="fixed">Fixed (ignore basis)</option>
            </select>
          </Field>
          {row("tds_percentage", "TDS % (tax withheld)", { type: "number", step: "0.01" })}
        </div>
      </div>
    );
  }
  return null;
}

function ActionsPanel({ actions, setActions, stopProcessing, setStopProcessing, actionTargetFields }) {
  const present = ACTION_KINDS.filter((k) => actions[k.key]);
  const missing = ACTION_KINDS.filter((k) => !actions[k.key]);
  const defaults = {
    price_override: {
      unit_price: "",
      currency_code: "",
      reason: "",
    },
    discount: {
      discount_type: "PERCENTAGE",
      discount_value: "",
      max_discount_amount: "",
      reason: "",
    },
    tax: {
      tax_percentage: "",
      tax_type: "GST",
      reason: "",
    },
    markup: {
      markup_percentage: "",
      reason: "",
    },
    approval: {
      approval_required: true,
      approval_role: "Billing Manager",
      approval_reason: "",
    },
    remark: {
      message: "",
      severity: "INFO",
    },
    set_billing_category: {
      category: "",
    },
    doctor_payout: {
      payout_role: "ADMITTING_DOCTOR",
      payout_type: "PERCENTAGE",
      payout_value: "",
      basis_amount: "ledger.net_amount",
      tds_percentage: "",
      recipient_provider_field: "encounter.provider_id",
    },
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 16 }}>
      <div>
        <div style={{ ...cardStyle, padding: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>THEN apply these actions</div>
          {present.length === 0 && (
            <div style={{ textAlign: "center", padding: 32, fontSize: 12, color: C.textSub, border: `2px dashed ${C.border}`, borderRadius: 8 }}>
              No actions yet — add from the panel on the right
            </div>
          )}
          {present.map((k) => (
            <div key={k.key} style={{ marginBottom: 12, padding: 12, border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>{k.label}</span>
                <Btn variant="danger" size="sm" onClick={() => { const n = { ...actions }; delete n[k.key]; setActions(n); }}>Remove</Btn>
              </div>
              <ActionEditor
                kind={k.key}
                value={actions[k.key]}
                onChange={(p) => setActions({ ...actions, [k.key]: p })}
                actionTargetFields={actionTargetFields}
              />
            </div>
          ))}
        </div>
        <div style={{ ...cardStyle, padding: 12, marginTop: 12, background: "#fff8e1" }}>
          <label style={{ display: "flex", gap: 10, fontSize: 13, alignItems: "flex-start" }}>
            <input type="checkbox" checked={stopProcessing} onChange={(e) => setStopProcessing(e.target.checked)} style={{ marginTop: 3 }} />
            <span><b>Stop Processing</b> — no further rules run when this rule matches</span>
          </label>
        </div>
      </div>
      <div style={{ ...cardStyle, padding: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Add action</div>
        {missing.map((k) => (
          <Btn key={k.key} variant="outline" size="sm" style={{ width: "100%", marginBottom: 6, justifyContent: "flex-start" }} onClick={() => setActions({ ...actions, [k.key]: defaults[k.key] })}>
            <SvgIcon name="plus" size={12} color={C.primary} /> {k.label}
          </Btn>
        ))}
        {missing.length === 0 && <div style={{ fontSize: 11, color: C.textSub }}>All types added</div>}
      </div>
    </div>
  );
}

function JsonPanel({ json }) {
  const text = useMemo(() => JSON.stringify(json, null, 2), [json]);
  const copy = () => navigator.clipboard.writeText(text);
  const download = () => {
    const blob = new Blob([text], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rule.json";
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>Generated Rule JSON</span>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="outline" size="sm" onClick={copy}>Copy</Btn>
          <Btn variant="outline" size="sm" onClick={download}><SvgIcon name="download" size={12} color={C.text} /> Download</Btn>
        </div>
      </div>
      <pre style={{ margin: 0, padding: 16, fontSize: 11, fontFamily: "monospace", overflow: "auto", maxHeight: 480, background: "#f8f9fa" }}>{text}</pre>
    </div>
  );
}

function TraceView({ trace }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, marginLeft: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
        {trace.node.type === "all" ? "AND" : trace.node.type === "any" ? "OR" : "NOT"} — {trace.result ? "passed" : "failed"}
      </div>
      {trace.children.map((c, i) =>
        "children" in c ? (
          <TraceView key={i} trace={c} />
        ) : (
          <div key={i} style={{ fontSize: 11, padding: "6px 8px", marginBottom: 4, borderRadius: 6, background: c.result === "pass" ? "#d1f2db" : c.result === "fail" ? "#fdecea" : "#f1f5f9" }}>
            {fieldDef(c.node.field)?.label ?? c.node.field} → {c.result} (actual: {JSON.stringify(c.actual)})
          </div>
        ),
      )}
    </div>
  );
}

function defaultSimContext(catalog) {
  const base = catalog?.sample_simulation_context ?? SAMPLE_CONTEXT;
  const ce = base.charge_event ?? {};
  return {
    ...base,
    charge_event: {
      ...ce,
      event_time: ce.event_time ?? new Date().toISOString(),
    },
  };
}

function SimulationPanel({ rule, catalog, flash }) {
  const [text, setText] = useState(() => JSON.stringify(defaultSimContext(catalog), null, 2));
  const [run, setRun] = useState(null);
  const [serverResult, setServerResult] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  const [parseError, setParseError] = useState(null);

  const ctxObj = useMemo(() => {
    try {
      setParseError(null);
      return JSON.parse(text);
    } catch (e) {
      setParseError(String(e));
      return null;
    }
  }, [text]);

  const simulate = async () => {
    if (!ctxObj) return;
    const trace = evalGroup(rule.conditions, ctxObj);
    setRun({ trace, matched: trace.result });
    setSimBusy(true);
    setServerResult(null);
    try {
      const payload = {
        patient: ctxObj.patient,
        encounter: ctxObj.encounter,
        charge_event: {
          ...ctxObj.charge_event,
          event_time: ctxObj.charge_event?.event_time ?? new Date().toISOString(),
        },
        log: false,
      };
      const res = await evaluateManual(payload);
      setServerResult(res.evaluation ?? res);
      flash?.("Server evaluation complete", "success");
    } catch (err) {
      flash?.(err.message || "Server simulation failed", "error");
    } finally {
      setSimBusy(false);
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <div style={cardStyle}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700 }}>Input Context</span>
          <Btn size="sm" onClick={simulate} disabled={simBusy}>
            <SvgIcon name="flask" size={14} color="#fff" /> {simBusy ? "Running…" : "Run"}
          </Btn>
        </div>
        <div style={{ padding: 12 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {Object.keys(SIM_PRESETS).map((p) => (
              <Btn key={p} variant="outline" size="sm" onClick={() => {
                const preset = { ...SIM_PRESETS[p], charge_event: { ...SIM_PRESETS[p].charge_event, event_time: new Date().toISOString() } };
                setText(JSON.stringify(preset, null, 2));
              }}>{p}</Btn>
            ))}
          </div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={16} style={{ ...inputStyle, fontFamily: "monospace", fontSize: 11 }} />
          {parseError && <div style={{ fontSize: 11, color: C.danger, marginTop: 6 }}>{parseError}</div>}
        </div>
      </div>
      <div style={cardStyle}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, fontWeight: 700 }}>Simulation Result</div>
        <div style={{ padding: 16 }}>
          {!run && <div style={{ fontSize: 12, color: C.textSub }}>Run simulation to see evaluation trace</div>}
          {run && (
            <>
              <div style={{
                padding: 12, borderRadius: 8, marginBottom: 12,
                background: run.matched ? "#d1f2db" : "#fdecea",
                color: run.matched ? "#145a32" : "#842029",
                fontWeight: 600,
              }}>
                {run.matched ? "Rule matched" : "Rule did not match"} — {rule.rule_name}
              </div>
              <TraceView trace={run.trace} />
              {run.matched && (
                <pre style={{ marginTop: 12, padding: 10, fontSize: 10, background: "#f8f9fa", borderRadius: 8, overflow: "auto" }}>
                  {JSON.stringify({ matched: true, applied_actions: buildRuleJSON(rule).actions }, null, 2)}
                </pre>
              )}
              {serverResult && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Server evaluation</div>
                  <pre style={{ padding: 10, fontSize: 10, background: "#f0f9ff", borderRadius: 8, overflow: "auto", border: `1px solid ${C.border}` }}>
                    {JSON.stringify(serverResult, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplatesModal({ open, onClose, onPick }) {
  return (
    <Modal open={open} onClose={onClose} title="Rule Templates" description="Start from a common hospital billing rule.">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {TEMPLATES.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => { onPick(t.key); onClose(); }}
            style={{ textAlign: "left", padding: 12, borderRadius: 10, border: `1px solid ${C.border}`, background: "#fff", cursor: "pointer" }}
          >
            <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
            <div style={{ marginTop: 6 }}><RuleTypeChip type={t.type} /></div>
          </button>
        ))}
      </div>
    </Modal>
  );
}

export default function RuleBuilderScreen({ initial, setName, catalog, saving, onBack, onSave, flash }) {
  const [rule, setRule] = useState(initial);
  const [tab, setTab] = useState("builder");
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const fieldGroups = useMemo(
    () => enrichFieldGroups(catalog?.field_groups?.length ? catalog.field_groups : FIELD_GROUPS),
    [catalog],
  );
  const categories = catalog?.categories?.length ? catalog.categories : CATEGORIES;
  const ruleTypes = catalog?.rule_types?.length ? catalog.rule_types : RULE_TYPES;
  const operators = useMemo(() => resolveOperators(catalog?.operators), [catalog]);

  const actionTargetFields = useMemo(() => getActionTargetFields(fieldGroups), [fieldGroups]);

  const errors = useMemo(() => validateRule(rule), [rule]);
  const json = useMemo(() => buildRuleJSON(rule), [rule]);
  const update = (patch) => setRule((r) => ({ ...r, ...patch }));

  const handleSave = async () => {
    await onSave(rule);
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${rule.rule_code || "rule"}.json`;
    a.click();
    URL.revokeObjectURL(url);
    flash?.("JSON exported", "success");
  };

  const tabs = [
    { id: "builder", label: "Builder", icon: "sparkle" },
    { id: "actions", label: "Actions", icon: "bill" },
    { id: "json", label: "JSON Preview", icon: "sliders" },
    { id: "simulation", label: "Simulation", icon: "flask" },
  ];

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <div>
          <button type="button" onClick={onBack} style={{ border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: C.textSub, fontSize: 12, marginBottom: 6, padding: 0 }}>
            <SvgIcon name="back" size={14} color={C.textSub} /> Back to rules
          </button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Hospital Billing Rule Builder</h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textSub }}>Rule Set: <b>{setName}</b> · Version v1.0</p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <Btn variant="outline" size="sm" onClick={() => setTemplatesOpen(true)}><SvgIcon name="sparkle" size={14} color={C.primary} /> Templates</Btn>
          <Btn variant="outline" size="sm" onClick={() => flash?.(errors.length ? `Validation failed: ${errors.join(" · ")}` : "Validation passed", errors.length ? "error" : "success")}>
            <SvgIcon name="check" size={14} color={C.text} /> Validate
          </Btn>
          <Btn variant="outline" size="sm" onClick={() => setTab("simulation")}><SvgIcon name="flask" size={14} color={C.text} /> Simulate</Btn>
          <Btn variant="outline" size="sm" onClick={exportJSON}><SvgIcon name="download" size={14} color={C.text} /> Export</Btn>
          <Btn size="sm" onClick={handleSave} disabled={saving}>
            <SvgIcon name="check" size={14} color="#fff" /> {saving ? "Saving…" : "Save Rule"}
          </Btn>
        </div>
      </div>

      {errors.length > 0 && (
        <div style={{ padding: 10, borderRadius: 8, background: "#fdecea", border: "1px solid #f5c2c7", fontSize: 12, color: C.danger, marginBottom: 12, display: "flex", gap: 8 }}>
          <SvgIcon name="alert-triangle" size={16} color={C.danger} />
          <span><b>{errors.length} issue(s):</b> {errors.join(" · ")}</span>
        </div>
      )}

      <TabBar tabs={tabs} value={tab} onChange={setTab} />

      {tab === "builder" && (
        <div style={{ display: "grid", gridTemplateColumns: "240px 1fr 200px", gap: 16 }}>
          <FieldsSidebar fieldGroups={fieldGroups} categories={categories} />
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
            <MetadataForm rule={rule} update={update} ruleTypes={ruleTypes} />
            <div style={cardStyle}>
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Visual Rule Builder</div>
                <div style={{ fontSize: 11, color: C.textSub }}>Drag fields or add conditions · IF groups (AND · OR · NOT)</div>
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ ...labelStyle, marginBottom: 8 }}>IF the following conditions match</div>
                <GroupView node={rule.conditions} onChange={(g) => update({ conditions: g })} isRoot fieldGroups={fieldGroups} operators={operators} />
              </div>
            </div>
          </div>
          <BlocksSidebar />
        </div>
      )}

      {tab === "actions" && (
        <ActionsPanel
          actions={rule.actions}
          setActions={(a) => update({ actions: a })}
          stopProcessing={rule.stop_processing}
          setStopProcessing={(v) => update({ stop_processing: v })}
          actionTargetFields={actionTargetFields}
        />
      )}

      {tab === "json" && <JsonPanel json={json} />}
      {tab === "simulation" && <SimulationPanel rule={rule} catalog={catalog} flash={flash} />}

      <TemplatesModal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        onPick={(k) => {
          setRule({ ...makeTemplate(k), rule_set_id: rule.rule_set_id });
          flash?.("Template loaded", "success");
        }}
      />
    </div>
  );
}
